import type { CarConfigDispatchType, CarConfigStateType } from "../redux/store";
import { checkDiff, commonQueryParams, find } from "../../../../common-deprecated/utils";
import CarDataConsolidator from "../../../shared-logic/utils/classes/CarDataConsolidator";
import type { ExtraType, FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import { addExtra, removeExtra, setWheels, updateExtraSetItems } from "../redux/slices/CarConfigBasketSlice";
import Debug from "../../../../common-deprecated/Debug";
import type { QueryType } from "../../../../common-deprecated/server/types";
import * as History from "../../../../common-deprecated/History";
import { minimizeBasket } from "../../../shared-logic/utils/basketUtils";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { CarConfigSettingsType } from "../redux/slices/CarConfigSettingsSlice";
import type { TradeInAemExternalDataType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import { isAllowedFinanceProductForTradeIn } from "./carConfigSettingsUtils";
import { setTradeInAemIncompatible } from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { setBasketParams } from "../redux/slices/CarConfigNavigationSlice";

/**
 * Method which resolves the logic when an extra's setItems are updated due to colour dependencies
 * @param getState
 * @param dispatch
 */
export const resolveSetItemColourDependencies = (
    getState: () => CarConfigStateType,
    dispatch: CarConfigDispatchType,
): void => {
    const { items, basket } = getState();
    const { extras, colour, upholstery, primaryAccentColour, secondaryAccentColour } = basket;

    // Get all the selected extras with sets
    extras.forEach((extra) => {
        const itemExtra = find(extra.id, items.extras);
        // Check if the setItems of each extra still validate for the current selected colour & upholstery
        if (itemExtra && itemExtra.setItems.length) {
            const colourDependendSetItems = itemExtra.setItems.filter(
                (setItem) =>
                    // Only show extras which are available for the current colour & upholstery combination.
                    CarDataConsolidator.isAvailable(setItem, colour, upholstery).available &&
                    // Also check the colouringmode
                    CarDataConsolidator.validatesForColourMode(setItem as ExtraType, {
                        colour,
                        primaryAccentColour,
                        secondaryAccentColour,
                    }),
            );
            // Check both the difference in the basket.extras[x]
            if (checkDiff(extra.setItems, colourDependendSetItems)) {
                dispatch(updateExtraSetItems({ extra: itemExtra, setItems: colourDependendSetItems }));
            }
        }
    });
};

/**
 * Method which resolves the include exclude rules of adding an extra
 * @param getState
 * @param extra
 * @param dispatch
 */
export const resolveAddExtraIncludExcludeRules = (
    getState: () => CarConfigStateType,
    extra: ExtraType,
    dispatch: CarConfigDispatchType,
): void => {
    const { items, basket } = getState();
    const childOption = CarDataConsolidator.getSelectedExtraOption(extra);

    // Remove extras excluded by the selected extra, its child-option & its set-items
    CarDataConsolidator.getExcludedExtras(extra, basket.extras, childOption).forEach((selectedExtra) =>
        dispatch(removeExtra({ extra: selectedExtra })),
    );

    // Add non conflicting included extras.
    CarDataConsolidator.getNonConflictingIncludedExtras(extra, items.extras, basket).forEach((includedExtra) =>
        dispatch(addExtra({ extra: includedExtra, automatic: true })),
    );

    // Extra -> include/exclude a wheel
    if (!extra.isWheel) {
        // If the (child)extra includes a wheel, select it.
        const includedWheel = items.wheels.find((wheel) => {
            return childOption
                ? extra.included.concat(childOption.included).find((includedExtra) => wheel.id === includedExtra)
                : extra.included.find((includedExtra) => wheel.id === includedExtra);
        });
        if (includedWheel && includedWheel.id !== basket.wheel.id) {
            dispatch(setWheels({ wheel: includedWheel }));
        }
        // If the extra excludes a wheel(s), make sure the default first available wheel is selected
        const excludedWheels = items.wheels.filter((wheel) => {
            return childOption
                ? extra.excluded.concat(childOption.excluded).find((excludedExtra) => wheel.id === excludedExtra)
                : extra.excluded.find((excludedExtra) => wheel.id === excludedExtra);
        });
        if (excludedWheels.length && find(basket.wheel.id, excludedWheels)) {
            const firstAvailableWheel = items.wheels.filter((wheel) => !find(wheel.id, excludedWheels))[0];
            if (firstAvailableWheel) {
                dispatch(setWheels({ wheel: firstAvailableWheel }));
            } else Debug.error(`No wheel found for selection due to exclude rules defined in: ${extra.name}`);
        }
    }
};

/**
 * Method which resolves the include exclude rules of removing an extra
 * @param getState
 * @param removedExtra
 * @param dispatch
 */
export const resolveRemoveExtraIncludeExcludeRules = (
    getState: () => CarConfigStateType,
    removedExtra: ExtraType,
    dispatch: CarConfigDispatchType,
): void => {
    const { items } = getState();
    let { basket } = getState();
    const childOption = CarDataConsolidator.getSelectedExtraOption(removedExtra);

    // Returns a list of extras which have includes towards the provided extra
    const includedBy = CarDataConsolidator.getExtrasIncludedBy(removedExtra, basket.extras, childOption);

    // Get the extras in the basket included by this extra.
    const includes = CarDataConsolidator.getIncludedExtras(removedExtra, basket.extras)
        // Filter the user selected extras
        .filter((includedExtra) => !includedExtra.userSelected)
        // filter extras included by other selected extras or wheel.
        .filter(
            (includedExtra) =>
                !basket.extras
                    .filter((selectedExtra) => selectedExtra.id !== removedExtra.id)
                    .find((selectedExtra) => selectedExtra.included.includes(includedExtra.id)) &&
                !basket.wheel.included.includes(includedExtra.id),
        );

    includedBy.concat(includes).forEach((includedExtra) => dispatch(removeExtra({ extra: includedExtra })));

    // If the currently removed item is not a wheel, we have to take care of possible wheel includes.
    // If the removed extra is part of the wheel currently in the basket, the wheel should be deselected.
    // However, There are some edge cases where we don't want to execute this wheel include logic:

    if (!removedExtra.isWheel) {
        // Important: Refresh basket state as it is possible that the wheel in the basket is already updated because of
        // the include/exclude rules of the removeExtra dispatch earlier in this function.
        basket = getState().basket; // eslint-disable-line prefer-destructuring
        const includedByWheel = basket.wheel.included.find((wheelInclude) => {
            return childOption
                ? wheelInclude === removedExtra.id || wheelInclude === childOption.id
                : wheelInclude === removedExtra.id;
        });
        if (includedByWheel) {
            // Get the first wheel which doesn't have this extra included
            const newWheel = items.wheels.find((wheel) => {
                return !wheel.included.find((wheelInclude) => {
                    return childOption
                        ? wheelInclude === removedExtra.id || wheelInclude === childOption.id
                        : wheelInclude === removedExtra.id;
                });
            });
            if (newWheel) dispatch(setWheels({ wheel: newWheel }));
            else Debug.error(`No wheel found to fall back to which doesn't include: ${removedExtra.name}`);
        }
    }
};

export type ParamModificationType = { param: "p" | "e"; action: "filter" | "set"; shortIds: string[] };

/**
 * Method which sets the query url with History.pushQueryParams() AND updates the commonSettings.query state.
 *
 * Additionally this method can further modify the query params by using the optional paramaters described below.
 *
 * ! Calling this util multiple times during a single action might result in a faulty outcome.
 * ! This is because the basket's data might not propagate correctly throughout multiple iterations.
 * ! If needed, use multiple paramModifications instead.
 *
 * @param carConfigQueries
 * @param dispatch
 * @param basket - Needed to generate a minimized basket.
 * @param car - Needed to generate a minimized basket.
 * @param path - The current path, this is needed to update the url with History.pushQueryParams.
 * @param modify - Optional parameter, this can be used in order to further modify the params.
 * @param modify.param - The following query params will be modified. Currently only used for query params "p" and "e.
 * @param modify.action - "set" will override any params that match modify.param with the given shortIds, "filter" will filter out the given shortIds.
 * @param modify.shortIds - The ids that will be used to modify the query params.
 */
export const modifyQueryParams = (
    carConfigQueries: QueryType,
    dispatch: CarConfigDispatchType,
    basket: BasketType,
    car: CarType,
    path: string,
    paramModifications?: ParamModificationType[],
): void => {
    const params: Record<string, unknown> = minimizeBasket(basket, car);
    const allowedQueryTypes = ["financeOption", "useLbxJourney", ...commonQueryParams];

    (Object.keys(carConfigQueries) as (keyof QueryType)[])
        .filter((queryKey) => allowedQueryTypes.includes(queryKey))
        .forEach((key) => {
            params[key] = carConfigQueries[key];
        });

    paramModifications?.forEach((modification) => {
        if (modification.action === "filter") {
            const modifiedParams = params?.[modification.param] as string[];
            if (modifiedParams) {
                params[modification.param] = modifiedParams.filter(
                    (item) => !modification.shortIds.includes(String(item)),
                );
            }
        } else if (modification.action === "set") {
            params[modification.param] = modification.shortIds;
        }
    });

    History.replaceQueryParams({ path, ...params });
    dispatch(setBasketParams(params));
};

/**
 * Checks whether the current trade-in configuration is compatible with the selected finance option.
 * Note that this also gets set in the trade-in reducer.
 */
export const checkAndSetFinanceProductTradeInCompatibility = (
    tradeInData: TradeInAemExternalDataType,
    previousIncompatibleValue: boolean,
    financeOption: FinanceOptionType,
    financeProductName: string | undefined,
    bnbSettings: CarConfigSettingsType,
    dispatch: CarConfigDispatchType,
): void => {
    const incompatible =
        !!tradeInData.vehicleValue &&
        financeOption === "monthly" &&
        !!financeProductName &&
        !isAllowedFinanceProductForTradeIn(bnbSettings?.disableTradeInForFinanceProducts, financeProductName);
    if (previousIncompatibleValue !== incompatible) dispatch(setTradeInAemIncompatible(incompatible));
};
